/**
 * 牌型评估器。见 docs/ARCHITECTURE.md §4.2。
 *
 * 这是全项目正确性的地基：边池分配、摊牌、胜率蒙特卡洛全部压在这几个函数上。
 * 设计上只有一个对外承诺——**两手牌的 `HandRankValue` 完全相等 ⇔ 摊牌平分**。
 * 不存在任何花色破平逻辑，分池只看整数比较。
 *
 * 为什么是「单遍位图直方图」而不是常见的 7462 项完美哈希表：
 * 后者要么带一张几 MB 的预计算表（客户端首屏加载不可接受），要么运行期构建
 * （启动几十毫秒）。位图法零表（只有 8KB 顺子表）、无分配，实测足以满足
 * §4.2 的性能门槛（rank7 ≥ 1.5M 次/秒）。
 *
 * 铁律 #1：本模块全部是纯函数，无模块级可变状态，可安全并发调用。
 */
import type { CardId } from '../ids.js'
import { NUM_CARDS, type RankIndex, isValidCardIdFor, makeCard, rankOf, suitOf } from './cards.js'
import { highBit, popcount, straightHigh, straightHighShort } from './tables.js'
import { type DeckVariant, HandCategory, type HandRank, type HandRankValue, InvariantError } from './types.js'

// ============================================================================
// 编码 / 解码
// ============================================================================

/** `value = (category << 20) | (r1 << 16) | (r2 << 12) | (r3 << 8) | (r4 << 4) | r5` */
const CATEGORY_SHIFT = 20
const SLOT_SHIFTS = [16, 12, 8, 4, 0] as const
/** 短牌牌力值的标记位（见 types.ts 的 HandRankValue 注释） */
const SHORT_DECK_BIT = 1 << 24

/** 短牌里同花与葫芦互换强弱：category 字段存的是强弱序，读出时换回牌型 */
function swapFlushFullHouse(cat: number): number {
  return cat === HandCategory.Flush ? HandCategory.FullHouse : cat === HandCategory.FullHouse ? HandCategory.Flush : cat
}

export function categoryOf(v: HandRankValue): HandCategory {
  const cat = (v >> CATEGORY_SHIFT) & 0xf
  return ((v & SHORT_DECK_BIT) !== 0 ? swapFlushFullHouse(cat) : cat) as HandCategory
}

/** 牌型在某个玩法里的强弱序（短牌里同花在葫芦之上）。比较「牌型有没有变强」用它，别直接比枚举值 */
export function categoryStrength(category: HandCategory, variant: DeckVariant): number {
  return variant === 'shortDeck' ? swapFlushFullHouse(category) : category
}

/** 这个牌力值是不是按短牌规则算出来的 */
export function isShortDeckValue(v: HandRankValue): boolean {
  return (v & SHORT_DECK_BIT) !== 0
}

/** 标准编码 → 短牌编码：打标记位，category 换成短牌的强弱序 */
function toShortDeck(v: number): HandRankValue {
  const cat = (v >> CATEGORY_SHIFT) & 0xf
  return (SHORT_DECK_BIT | (swapFlushFullHouse(cat) << CATEGORY_SHIFT) | (v & 0xfffff)) as HandRankValue
}

/** 全部 5 个 slot（含恒为 0 的无效位），主要给调试与测试用 */
export function rankSlotsOf(v: HandRankValue): [number, number, number, number, number] {
  return [(v >> 16) & 0xf, (v >> 12) & 0xf, (v >> 8) & 0xf, (v >> 4) & 0xf, v & 0xf]
}

/** 各 category 的有效 slot 数，索引即 HandCategory 的数值 */
const SLOT_COUNTS: readonly number[] = [
  5, // HighCard
  4, // Pair
  3, // TwoPair
  3, // Trips
  1, // Straight
  5, // Flush
  2, // FullHouse
  2, // Quads
  1, // StraightFlush
]

/**
 * 皇家同花顺不是独立 category，只是 r1 === 12 的同花顺。
 * 单独开一类会破坏「value 相等 ⇔ 平分」的单一比较语义。
 */
export function isRoyalFlush(v: HandRankValue): boolean {
  return categoryOf(v) === HandCategory.StraightFlush && ((v >> 16) & 0xf) === 12
}

/** 红线 #28：牌力比较只走数值，绝不对字符串枚举用 >= */
export function compareRanks(a: HandRankValue, b: HandRankValue): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0
}

/** 把 mask 里从高到低的 n 个点数依次填进从 slotIndex 开始的 slot */
function appendTop(value: number, slotIndex: number, mask: number, n: number): number {
  let v = value
  let m = mask
  for (let i = 0; i < n; i++) {
    const b = highBit(m)
    v |= b << SLOT_SHIFTS[slotIndex + i]!
    m &= ~(1 << b)
  }
  return v
}

// ============================================================================
// 核心分类
// ============================================================================

/**
 * 关键引理（n ≤ 7）：「存在同花」与「存在四条或葫芦」互斥。
 *
 * 证明：四条占去 4 张牌且四种花色各一张，剩下至多 3 张，故任一花色至多 3 < 5；
 * 葫芦（3+2 或 3+3）中同一点数至多向某一花色贡献 1 张，三个点数至多 3 < 5。∎
 *
 * 正因为这条引理，同花分支才能直接 return 而不必先检查四条/葫芦。对合法的 n <= 7
 * 输入这个断言永远不会触发——留着它是为了让「引理不再成立」这件事有人看门：
 * 哪天有人把 n 放宽到 8 张、改做奥马哈、或把花色计数换回计数器实现（重复牌会让
 * 计数器虚高而位图不会），这里会立刻炸，而不是静默给出错误的牌力。
 * 成本是同花分支里的几次位运算，只占全部手牌的约 3%。
 */
function assertFlushLemma(m2: number, m3: number, m4: number): void {
  const trips = m3 & ~m4
  const pairs = m2 & ~m3
  const hasFullHouse = trips !== 0 && ((trips & (trips - 1)) !== 0 || pairs !== 0)
  if (m4 !== 0 || hasFullHouse) {
    throw new InvariantError(
      'evaluator: 同花与四条/葫芦同时成立，n<=7 的互斥引理已被打破（输入超过 7 张？）',
    )
  }
}

/**
 * 直方图 → HandRankValue。
 *
 * @param m1..m4 出现 >=1 / >=2 / >=3 / >=4 次的点数位图
 * @param flushMask 同花花色的点数位图；无同花时为 0（合法同花位图必有 >=5 个置位，故 0 无歧义）
 */
function classify(m1: number, m2: number, m3: number, m4: number, flushMask: number, short: boolean): HandRankValue {
  const v = classifyStandardOrder(m1, m2, m3, m4, flushMask, short ? straightHighShort : straightHigh)
  // ★ 7 张里同花与葫芦 / 四条互斥（见 assertFlushLemma），所以「这手算成什么牌型」两种玩法一致，
  //   不同的只是玩家之间谁大 —— 编码时换一下强弱序即可
  return short ? toShortDeck(v) : v
}

function classifyStandardOrder(
  m1: number,
  m2: number,
  m3: number,
  m4: number,
  flushMask: number,
  straightOf: (mask: number) => number,
): HandRankValue {
  // 1. 同花 / 同花顺
  if (flushMask !== 0) {
    assertFlushLemma(m2, m3, m4)
    const sf = straightOf(flushMask)
    if (sf >= 0) return ((HandCategory.StraightFlush << CATEGORY_SHIFT) | (sf << 16)) as HandRankValue
    return appendTop(HandCategory.Flush << CATEGORY_SHIFT, 0, flushMask, 5) as HandRankValue
  }

  // 2. 四条
  if (m4 !== 0) {
    const q = highBit(m4)
    const v = (HandCategory.Quads << CATEGORY_SHIFT) | (q << 16)
    return appendTop(v, 1, m1 & ~(1 << q), 1) as HandRankValue
  }

  const trips = m3 & ~m4
  const pairs = m2 & ~m3

  // 3. 葫芦必须先于顺子判定：3+2 的牌里完全可能同时藏着顺子
  if (trips !== 0) {
    const t = highBit(trips)
    // 第二组三条也能当对子用（7 张里 AAA-KKK-x 是 A 满 K）
    const rest = (trips & ~(1 << t)) | pairs
    if (rest !== 0) {
      return ((HandCategory.FullHouse << CATEGORY_SHIFT) | (t << 16) | (highBit(rest) << 12)) as HandRankValue
    }
  }

  // 4. 顺子
  const st = straightOf(m1)
  if (st >= 0) return ((HandCategory.Straight << CATEGORY_SHIFT) | (st << 16)) as HandRankValue

  // 5. 三条
  if (trips !== 0) {
    const t = highBit(trips)
    const v = (HandCategory.Trips << CATEGORY_SHIFT) | (t << 16)
    return appendTop(v, 1, m1 & ~(1 << t), 2) as HandRankValue
  }

  // 6. 两对 / 一对 / 高牌
  const np = popcount(pairs)
  if (np >= 2) {
    const p1 = highBit(pairs)
    const p2 = highBit(pairs & ~(1 << p1))
    // 7 张三对时，第三对的点数也参与踢脚竞争，所以踢脚要从 m1 里取而不是从「非对子」里取
    const kick = highBit(m1 & ~(1 << p1) & ~(1 << p2))
    return ((HandCategory.TwoPair << CATEGORY_SHIFT) |
      (p1 << 16) |
      (p2 << 12) |
      (kick << 8)) as HandRankValue
  }
  if (np === 1) {
    const p = highBit(pairs)
    const v = (HandCategory.Pair << CATEGORY_SHIFT) | (p << 16)
    return appendTop(v, 1, m1 & ~(1 << p), 3) as HandRankValue
  }
  return appendTop(HandCategory.HighCard << CATEGORY_SHIFT, 0, m1, 5) as HandRankValue
}

// ============================================================================
// 对外入口
// ============================================================================

/**
 * 选出同花花色的点数位图，无同花返回 0。
 *
 * n <= 7 时至多一门花色能凑够 5 张，所以「第一门 >=5 的」就是唯一解。
 * 合法的同花位图必有 >= 5 个置位，故用 0 表示「无同花」不会和真同花混淆。
 */
function pickFlushMask(sm0: number, sm1: number, sm2: number, sm3: number): number {
  if (popcount(sm0) >= 5) return sm0
  if (popcount(sm1) >= 5) return sm1
  if (popcount(sm2) >= 5) return sm2
  if (popcount(sm3) >= 5) return sm3
  return 0
}


/**
 * 7 张牌的**无校验**热路径，供胜率蒙特卡洛调用（每次模拟数十万次）。
 *
 * 不做任何输入校验：参数必须是 0..51 的合法 CardId 且互不相同。
 * 传重复牌或越界值不会抛异常，只会静默算出一个无意义的牌力值——
 * 需要校验请走 `rankCards`，需要查重复请走 `hasDuplicates`。
 *
 * @param short 按短牌规则算（同花大于葫芦、A-6-7-8-9 是顺子）。同一次比较里的所有手牌必须用同一个值。
 */
export function rank7(
  a: number,
  b: number,
  c: number,
  d: number,
  e: number,
  f: number,
  g: number,
  short = false,
): HandRankValue {
  // 出现 >=1 / >=2 / >=3 / >=4 次的点数位图。四条语句的顺序不可换：
  // m4 依赖更新前的 m3，m3 依赖更新前的 m2，依此类推。
  let m1 = 0
  let m2 = 0
  let m3 = 0
  let m4 = 0
  // 每门花色的点数位图。无重复牌时同门花色内每个点数至多一张，
  // 于是「几张」== popcount(mask)，不必再维护四个计数器。
  let sm0 = 0
  let sm1 = 0
  let sm2 = 0
  let sm3 = 0

  // 七张全部展开而不是写循环：这是全项目最热的函数（蒙特卡洛内层），
  // 循环变量与逐张取牌的分支在这里都是纯开销。
  const ra = 1 << (a >> 2)
  m4 |= m3 & ra
  m3 |= m2 & ra
  m2 |= m1 & ra
  m1 |= ra
  const sa = a & 3
  if (sa === 0) sm0 |= ra
  else if (sa === 1) sm1 |= ra
  else if (sa === 2) sm2 |= ra
  else sm3 |= ra

  const rb = 1 << (b >> 2)
  m4 |= m3 & rb
  m3 |= m2 & rb
  m2 |= m1 & rb
  m1 |= rb
  const sb = b & 3
  if (sb === 0) sm0 |= rb
  else if (sb === 1) sm1 |= rb
  else if (sb === 2) sm2 |= rb
  else sm3 |= rb

  const rc = 1 << (c >> 2)
  m4 |= m3 & rc
  m3 |= m2 & rc
  m2 |= m1 & rc
  m1 |= rc
  const sc = c & 3
  if (sc === 0) sm0 |= rc
  else if (sc === 1) sm1 |= rc
  else if (sc === 2) sm2 |= rc
  else sm3 |= rc

  const rd = 1 << (d >> 2)
  m4 |= m3 & rd
  m3 |= m2 & rd
  m2 |= m1 & rd
  m1 |= rd
  const sd = d & 3
  if (sd === 0) sm0 |= rd
  else if (sd === 1) sm1 |= rd
  else if (sd === 2) sm2 |= rd
  else sm3 |= rd

  const re = 1 << (e >> 2)
  m4 |= m3 & re
  m3 |= m2 & re
  m2 |= m1 & re
  m1 |= re
  const se = e & 3
  if (se === 0) sm0 |= re
  else if (se === 1) sm1 |= re
  else if (se === 2) sm2 |= re
  else sm3 |= re

  const rf = 1 << (f >> 2)
  m4 |= m3 & rf
  m3 |= m2 & rf
  m2 |= m1 & rf
  m1 |= rf
  const sf = f & 3
  if (sf === 0) sm0 |= rf
  else if (sf === 1) sm1 |= rf
  else if (sf === 2) sm2 |= rf
  else sm3 |= rf

  const rg = 1 << (g >> 2)
  m4 |= m3 & rg
  m3 |= m2 & rg
  m2 |= m1 & rg
  m1 |= rg
  const sg = g & 3
  if (sg === 0) sm0 |= rg
  else if (sg === 1) sm1 |= rg
  else if (sg === 2) sm2 |= rg
  else sm3 |= rg

  return classify(m1, m2, m3, m4, pickFlushMask(sm0, sm1, sm2, sm3), short)
}

/**
 * 5/6/7 张。校验长度与 CardId 取值范围（短牌还校验不能有 2-5）；
 * **不**校验重复牌（成本由 `hasDuplicates` 承担）。
 */
export function rankCards(cards: readonly CardId[], variant: DeckVariant = 'standard'): HandRankValue {
  const n = cards.length
  if (n < 5 || n > 7) {
    throw new InvariantError(`rankCards: 只支持 5/6/7 张，收到 ${n} 张`)
  }

  let m1 = 0
  let m2 = 0
  let m3 = 0
  let m4 = 0
  let sm0 = 0
  let sm1 = 0
  let sm2 = 0
  let sm3 = 0

  for (let i = 0; i < n; i++) {
    const card = cards[i]!
    // 越界 id 会让 1 << r 溢出 13 位，把顺子表读出 undefined 并静默产出 NaN 牌力，
    // 那种错误要到分池时才暴露。这里多一次比较换早失败。
    if (!isValidCardIdFor(card, variant)) {
      throw new InvariantError(
        variant === 'shortDeck'
          ? `rankCards: 短牌里没有这张牌 ${String(card)}（合法区间 16..${NUM_CARDS - 1}）`
          : `rankCards: 非法 CardId ${String(card)}（合法区间 0..${NUM_CARDS - 1}）`,
      )
    }
    const bit = 1 << (card >> 2)
    m4 |= m3 & bit
    m3 |= m2 & bit
    m2 |= m1 & bit
    m1 |= bit
    const s = card & 3
    if (s === 0) sm0 |= bit
    else if (s === 1) sm1 |= bit
    else if (s === 2) sm2 |= bit
    else sm3 |= bit
  }

  return classify(m1, m2, m3, m4, pickFlushMask(sm0, sm1, sm2, sm3), variant === 'shortDeck')
}

// ============================================================================
// bestFive 回溯（§4.2「确定性输出，供 UI 高亮」）
// ============================================================================

/**
 * 顺子的五个点数，按展示顺序（高 → 低）。
 * 轮子固定为 5-4-3-2-A：A 在轮子里当 1，排在最后而不是最前。
 * 短牌的最小顺子固定为 9-8-7-6-A（「9 高」只可能是它：短牌没有 5）。
 */
function straightRanks(high: number, short: boolean): number[] {
  if (!short && high === 3) return [3, 2, 1, 0, 12]
  if (short && high === 7) return [7, 6, 5, 4, 12]
  return [high, high - 1, high - 2, high - 3, high - 4]
}

/** 各 category 的「显著性组」大小，与 HandRank.ranks 逐项对应 */
const GROUP_SIZES: readonly (readonly number[])[] = [
  [1, 1, 1, 1, 1], // HighCard
  [2, 1, 1, 1], //    Pair
  [2, 2, 1], //       TwoPair
  [3, 1, 1], //       Trips
  [], //              Straight（走 straightRanks）
  [1, 1, 1, 1, 1], // Flush（限定在同花花色内）
  [3, 2], //          FullHouse
  [4, 1], //          Quads
  [], //              StraightFlush（走 straightRanks）
]

/** 取该点数下 suitIndex 最小的 count 张牌 */
function takeLowestSuits(cards: readonly CardId[], rank: number, count: number, out: CardId[]): void {
  let taken = 0
  for (let s = 0; s < 4 && taken < count; s++) {
    const want = makeCard(rank, s)
    for (let i = 0; i < cards.length; i++) {
      if (cards[i] === want) {
        out.push(want)
        taken++
        break
      }
    }
  }
  if (taken < count) {
    throw new InvariantError(`evaluateHand: bestFive 回溯时点数 ${rank} 只找到 ${taken}/${count} 张`)
  }
}

function pickBestFive(
  cards: readonly CardId[],
  category: HandCategory,
  ranks: readonly number[],
  flushSuit: number,
  short: boolean,
): CardId[] {
  const out: CardId[] = []
  if (category === HandCategory.StraightFlush) {
    // 同花顺只从同花花色取牌：牌桌上可能同时存在同点数的别花色牌，取错了高亮就会飘
    for (const r of straightRanks(ranks[0]!, short)) out.push(makeCard(r, flushSuit))
    return out
  }
  if (category === HandCategory.Flush) {
    for (const r of ranks) out.push(makeCard(r, flushSuit))
    return out
  }
  if (category === HandCategory.Straight) {
    for (const r of straightRanks(ranks[0]!, short)) takeLowestSuits(cards, r, 1, out)
    return out
  }
  const sizes = GROUP_SIZES[category]!
  for (let i = 0; i < ranks.length; i++) {
    takeLowestSuits(cards, ranks[i]!, sizes[i]!, out)
  }
  return out
}

/**
 * 完整评估：value + category + 有效 slot + bestFive。
 *
 * 比 `rankCards` 慢一个数量级（要回溯具体牌面），只在摊牌 / UI 高亮时调用，
 * 绝不要放进蒙特卡洛内层循环。
 */
export function evaluateHand(cards: readonly CardId[], variant: DeckVariant = 'standard'): HandRank {
  const value = rankCards(cards, variant)
  const category = categoryOf(value)
  const slots = rankSlotsOf(value)
  const ranks = slots.slice(0, SLOT_COUNTS[category]!) as RankIndex[]

  // 同花/同花顺需要知道是哪门花色。这里必须和 rankCards 用**同一个**判据
  // （某门花色的不同点数 >= 5 种），否则两边对「算不算同花」的口径一旦分岔，
  // value 说是同花而 bestFive 找不到花色，就会变成一个只在特定牌面复现的崩溃。
  let flushSuit = -1
  if (category === HandCategory.Flush || category === HandCategory.StraightFlush) {
    const suitMasks = [0, 0, 0, 0]
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i]!
      suitMasks[suitOf(card)]! |= 1 << rankOf(card)
    }
    for (let s = 0; s < 4; s++) {
      if (popcount(suitMasks[s]!) >= 5) {
        flushSuit = s
        break
      }
    }
    if (flushSuit < 0) {
      throw new InvariantError('evaluateHand: 判定为同花但找不到 >=5 个点数的花色')
    }
  }

  return {
    value,
    category,
    ranks,
    bestFive: pickBestFive(cards, category, ranks, flushSuit, variant === 'shortDeck'),
  }
}

/** 便于测试与调试：把 bestFive 的点数按展示顺序取出 */
export function bestFiveRanks(rank: HandRank): RankIndex[] {
  return rank.bestFive.map(rankOf)
}
